import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { useAppContext } from "../../App.jsx";
import { useQueries } from "@tanstack/react-query";
import { q_prizebox_pop } from "../../queries/queries.jsx";
import { getv, nils } from "../../utils/utils.jsx";
import { Img } from "../../components/utilityComps.jsx";
import { metamaskimg } from "../../utils/links.jsx";
import _ from "lodash";
import { boxtypemap, gen_asset_i, ModernLoader, GlowButton } from "./dependencies.jsx";
import SpiteAnimator from "../../components/SpiteAnimator/SpiteAnimator.jsx";
import "./prizebox-animations.css";

export const PrizeboxPopupOverlay = ({ pcon, explosionWidth = 500, explosionHeight = 500 }) => {
  const appcon = useAppContext();
  const { psearch, upd_psearch } = appcon;

  const { showopen, set_showopen, box_error } = pcon;
  const boxopentx = psearch?.boxopentx;

  // New 4-stage system state
  const [stage, setStage] = useState(1);
  const [pumpText, setPumpText] = useState("");
  const [showClaimButton, setShowClaimButton] = useState(false);
  const [boxFadedOut, setBoxFadedOut] = useState(false);
  const [isBoxBouncing, setIsBoxBouncing] = useState(false);
  const [showExplosion, setShowExplosion] = useState(false);
  const [showPrizeText, setShowPrizeText] = useState(false);

  // Web3 integration (keep existing)
  const [qpop] = useQueries({
    queries: [
      q_prizebox_pop(
        { hash: boxopentx },
        !_.isEmpty(boxopentx) && boxopentx.startsWith("0x")
          ? { refetchInterval: 5 * 1e3, staleTime: 5 * 1e3 }
          : { refetchInterval: 60 * 1e3, staleTime: 60 * 1e3 },
      ),
    ],
  });

  const pdoc = useMemo(() => {
    let d = getv(qpop, "data.result");
    if (_.isEmpty(d)) return {};
    return d;
  }, [qpop.dataUpdatedAt]);

  // Asset generation (keep existing logic)
  const boximg = getv(boxtypemap, `${pdoc.boxtype}.img`);
  const prizeimg = gen_asset_i(pdoc.prize);

  // Stage detection logic
  useEffect(() => {
    if (box_error) {
      setStage("error");
    } else if (
      boxopentx === "waiting_hash" ||
      boxopentx === "confirm_metamask"
    ) {
      setStage(1);
    } else if (boxopentx && boxopentx.startsWith("0x") && _.isEmpty(pdoc)) {
      setStage(2);
    } else if (!_.isEmpty(pdoc)) {
      setStage(3);
    }
  }, [boxopentx, pdoc, box_error]);

  // Stage 2: Pump text animation
  const pumpMessages = [
    "Generating hash blocks",
    "Verifying merkle roots",
    "Decrypting smart contract",
    "Analyzing gas optimization",
    "Compiling bytecode",
    "Validating transaction",
    "Processing blockchain data",
    "Synchronizing network nodes",
  ];

  const startPumpAnimation = useRef(null);

  useEffect(() => {
    if (stage === 2) {
      let messageIndex = 0;
      const cycleText = () => {
        setPumpText(pumpMessages[messageIndex]);
        messageIndex = (messageIndex + 1) % pumpMessages.length;
      };

      cycleText(); // Initial message
      startPumpAnimation.current = setInterval(cycleText, 2000);
    } else {
      // Clear animation when leaving stage 2
      if (startPumpAnimation.current) {
        clearInterval(startPumpAnimation.current);
        startPumpAnimation.current = null;
      }
    }

    return () => {
      if (startPumpAnimation.current) {
        clearInterval(startPumpAnimation.current);
        startPumpAnimation.current = null;
      }
    };
  }, [stage]);

  // Don't render popup if boxopentx is null/empty (after all hooks)
  if (nils(boxopentx)) {
    return null;
  }

  // Stage 3 -> Stage 4 transition
  const handleBoxClick = () => {
    if (stage !== 3) return;

    // Start bouncing animation
    setIsBoxBouncing(true);

    // After 5 seconds, hide box and show explosion
    setTimeout(() => {
      setIsBoxBouncing(false);
      // Small delay to ensure box disappears before explosion
      setTimeout(() => {
        setShowExplosion(true);
      }, 50);
    }, 5000);
  };

  // Handle explosion animation complete
  const handleExplosionComplete = () => {
    setShowExplosion(false);
    setStage(4);
    setBoxFadedOut(true);
  };

  // Handle prize image animation complete
  const handlePrizeImageComplete = () => {
    // Show prize text after image scales back to 1x
    setTimeout(() => {
      setShowPrizeText(true);
      
      // Show claim button after additional 4 seconds
      setTimeout(() => {
        setShowClaimButton(true);
      }, 4000);
    }, 200);
  };

  // Close modal handler
  const close_popup = () => {
    set_showopen(false);
    upd_psearch({ boxopentx: null });
    if (pcon.set_box_error) pcon.set_box_error(null);

    // Reset all states
    setStage(1);
    setPumpText("");
    setShowClaimButton(false);
    setBoxFadedOut(false);
    setIsBoxBouncing(false);
    setShowExplosion(false);
    setShowPrizeText(false);
  };

  const modalContent = (
    <div className="fixed inset-0 z-[100] backdrop-blur-md bg-black/70">
      {/* Close Button - Top Right (Always Accessible) */}
      <div className="absolute top-6 right-6 z-[200]">
        <button
          onClick={close_popup}
          className="w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-black/50 transition-colors duration-200"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Modal Content - Centered */}
      <div className="w-full h-full flex items-center justify-center p-4">
        <div className="relative w-96 h-96 flex items-center justify-center">
          {/* Stage 1: Transaction Signing */}
          {stage === 1 && (
            <div className="text-center space-y-6 animate-fade-in-up">
              <div className="relative">
                <div className="absolute inset-0 bg-orange-500/30 blur-3xl animate-pulse rounded-full" />
                <div className="relative z-10 w-48 h-48 mx-auto">
                  <Img
                    img={metamaskimg}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <h3 className="text-orange-400 font-digi text-2xl">
                  Transaction Signing
                </h3>
                <p className="text-gray-300 text-lg">
                  Waiting to sign transaction and push to chain
                </p>
                <ModernLoader size="md" className="mx-auto mt-4" />
              </div>
            </div>
          )}

          {/* Stage 2: Processing Transaction */}
          {stage === 2 && (
            <div className="text-center space-y-6 animate-fade-in-up">
              <div className="relative">
                <div className="absolute inset-0 bg-blue-500/30 blur-3xl animate-pulse rounded-full" />
                <div className="relative z-10 w-48 h-48 mx-auto flex items-center justify-center">
                  <svg
                    width="120"
                    height="120"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="spin-anim"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-r3lig/30"
                    />
                    <path
                      d="M12 2a10 10 0 0 1 10 10"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="text-r3lig"
                    />
                  </svg>
                </div>
              </div>
              <div className="space-y-2">
                <h3 className="text-r3lig font-digi text-2xl animate-text-typing">
                  {pumpText}
                  <span className="moving-dots"></span>
                </h3>
                <p className="text-gray-300 text-lg">
                  Processing blockchain transaction
                </p>
              </div>
            </div>
          )}

          {/* Stage 3: Interactive Box */}
          {stage === 3 && !boxFadedOut && !showExplosion && (
            <div className="text-center space-y-6 animate-fade-in-up">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-r3lig/30 via-r3reg/20 to-r3dark/30 blur-2xl animate-pulse rounded-full scale-125" />
                {!isBoxBouncing && (
                  <motion.div
                    className="relative z-10 w-64 h-64 mx-auto cursor-pointer hover:scale-105 transition-transform duration-300"
                    onClick={handleBoxClick}
                    animate={{
                      x: [0, -2, 2, -1, 1, -1.5, 1.5, -0.5, 0.5, -1, 0],
                      y: [0, 1, -1, 2, -2, 1, -1, 1.5, -1.5, 0.5, 0],
                      rotate: [0, -0.5, 0.5, -0.3, 0.3, -0.2, 0.2, -0.1, 0.1, 0],
                    }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    <Img
                      img={boximg}
                      className="w-full h-full object-contain rounded-2xl shadow-2xl"
                    />
                    {/* Glow effect on hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-r3lig/20 to-r3reg/20 rounded-2xl opacity-0 hover:opacity-100 transition-opacity duration-300 blur-xl scale-110" />
                  </motion.div>
                )}
              </div>
              <div className="space-y-2">
                <h3 className="text-r3lig font-digi text-2xl">
                  {isBoxBouncing ? "Opening Prize Box..." : "Prize Box Ready!"}
                </h3>
                <p className="text-gray-300 text-lg">
                  {isBoxBouncing ? "Your prize is being revealed!" : "Click the box to reveal your prize"}
                </p>
              </div>
            </div>
          )}

          {/* Bouncing Box - Absolute positioned to viewport */}
          {isBoxBouncing && !showExplosion && (
            <motion.div
              className="fixed z-[150] w-64 h-64"
              style={{
                left: "50%",
                top: "50%",
                marginLeft: "-128px", // half of w-64
                marginTop: "-128px", // half of h-64
              }}
              animate={{
                x: [0, 350, -280, 150, -200, 400, -100, 250, -350, 80, -150, 300, -50, 0],
                y: [0, -180, 200, -250, 150, -100, 300, -200, 100, -300, 180, -80, 120, 0],
                rotate: [0, 180, -90, 360, -180, 270, -45, 540, -270, 90, -360, 180, -90, 0],
                scale: [1, 1.4, 0.6, 1.1, 0.9, 1.3, 0.7, 1.2, 0.8, 1.5, 0.5, 1.1, 0.9, 0.25],
              }}
              transition={{
                duration: 5,
                ease: "linear",
                times: [0, 0.08, 0.15, 0.23, 0.31, 0.38, 0.46, 0.54, 0.62, 0.69, 0.77, 0.85, 0.92, 1]
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Img
                img={boximg}
                className="w-full h-full object-contain rounded-2xl shadow-2xl"
              />
              {/* Enhanced glow during bouncing */}
              <div className="absolute inset-0 bg-gradient-to-br from-r3lig/40 to-r3reg/40 rounded-2xl blur-xl scale-125 animate-pulse" />
            </motion.div>
          )}


          {/* Explosion Animation - Center positioned */}
          {showExplosion && (
            <div className="fixed z-[150]" style={{
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)"
            }}>
              <SpiteAnimator
                spriteUrl="/spites/explosion.png"
                width={explosionWidth}
                height={explosionHeight}
                autoPlay={true}
                showControls={false}
                showFrameCounter={false}
                onAnimationComplete={handleExplosionComplete}
              />
            </div>
          )}

          {/* Stage 4: Prize Reveal */}
          {stage === 4 && (
            <div className="text-center space-y-6">
              {/* Prize Image with Framer Motion Animation */}
              <div className="relative">
                <motion.div
                  className="relative z-10 w-72 h-72 mx-auto"
                  initial={{ scale: 0 }}
                  animate={{ scale: [0, 1.2, 1] }}
                  transition={{
                    duration: 2,
                    times: [0, 0.7, 1],
                    ease: "easeOut"
                  }}
                  onAnimationComplete={handlePrizeImageComplete}
                >
                  <Img
                    img={prizeimg}
                    className="w-full h-full object-contain rounded-2xl shadow-2xl"
                  />
                  
                  {/* Golden Running Border */}
                  <motion.div
                    className="absolute inset-0 rounded-2xl"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 0] }}
                    transition={{
                      duration: 2,
                      times: [0, 0.7, 1],
                      ease: "easeOut"
                    }}
                    style={{
                      background: `linear-gradient(45deg, 
                        transparent 25%, 
                        #ffd700 25%, 
                        #ffd700 50%, 
                        transparent 50%, 
                        transparent 75%, 
                        #ffd700 75%, 
                        #ffd700
                      )`,
                      backgroundSize: '20px 20px',
                      animation: 'runningBorder 0.5s linear infinite',
                      border: '4px solid #ffd700',
                      boxShadow: '0 0 20px #ffd700, inset 0 0 20px rgba(255, 215, 0, 0.3)'
                    }}
                  />
                </motion.div>

                {/* Background glow effects */}
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/40 to-orange-500/40 blur-3xl animate-pulse rounded-full scale-150" />
                <div className="absolute inset-0 bg-gradient-to-br from-r3lig/30 to-purple-500/30 blur-2xl animate-pulse rounded-full scale-125" />
              </div>

              {/* Prize Won Text - Shows after image animation */}
              {showPrizeText && (
                <motion.div
                  className="space-y-4 mt-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                >
                  <motion.h2
                    className="text-yellow-400 font-digi text-4xl drop-shadow-2xl rounded-xl px-4 py-2"
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    transition={{ 
                      duration: 0.6,
                      type: "spring",
                      stiffness: 200
                    }}
                  >
                    🎉 PRIZE WON! 🎉
                  </motion.h2>
                  <motion.p
                    className="text-gray-300 text-xl font-medium"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                  >
                    Congratulations on your amazing prize!
                  </motion.p>

                  {/* Processing info - appears after additional delay */}
                  {showClaimButton && (
                    <motion.div
                      className="pt-4 space-y-4"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <div className="bg-r3dark/20 border border-r3lig/30 rounded-xl p-4 max-w-md mx-auto">
                        <p className="text-gray-300 text-sm leading-relaxed">
                          <span className="text-r3lig font-semibold">
                            🎁 Your prize is being processed!
                          </span>
                          <br />
                          It may take up to 20 minutes for your prize to be
                          delivered to your wallet.
                        </p>
                      </div>
                      <GlowButton
                        onClick={close_popup}
                        variant="secondary"
                        className="px-6 py-3 text-sm"
                      >
                        Close
                      </GlowButton>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </div>
          )}

          {/* Error State */}
          {stage === "error" && (
            <div className="text-center space-y-6 animate-fade-in-up">
              <div className="bg-red-500/20 border-2 border-red-500/50 rounded-xl p-8 max-w-sm w-full space-y-4">
                <div className="text-red-400 text-6xl animate-pulse">⚠️</div>
                <h3 className="text-red-300 font-digi text-xl">
                  Transaction Failed
                </h3>
                <p className="text-red-200 text-sm break-words">
                  {box_error || "An unknown error occurred"}
                </p>
                <GlowButton
                  onClick={close_popup}
                  variant="danger"
                  className="w-full"
                >
                  Close
                </GlowButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render modal in a portal for full viewport coverage
  return createPortal(modalContent, document.body);
};